import React, { useRef, useState } from "react";
import { api, inr } from "../lib/api";
import { X, UploadSimple, CheckCircle, Warning, FileCsv, Spinner } from "@phosphor-icons/react";

export default function BulkStockImportModal({ storeId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("set");
  const [defaultGst, setDefaultGst] = useState(5);
  const [autoCreate, setAutoCreate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("pick"); // pick | preview | done
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onFile = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true); setPreview(null);
    try {
      const form = new FormData(); form.append("file", f);
      const token = localStorage.getItem("pos_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/${storeId}/inventory/bulk-import/preview`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Preview failed");
      setPreview(data);
      setPhase("preview");
    } catch (e) {
      alert("Preview failed: " + e.message);
      setFile(null);
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!file) return;
    if (!window.confirm(`Import ${preview.total_rows} rows in '${mode === "set" ? "Set (overwrite qty)" : "Add (increment qty)"}' mode?`)) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      form.append("default_gst_rate", String(defaultGst));
      form.append("auto_create_skus", String(autoCreate));
      const token = localStorage.getItem("pos_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/${storeId}/inventory/bulk-import`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Import failed");
      setResult(data);
      setPhase("done");
    } catch (e) {
      alert("Import failed: " + e.message);
    } finally { setBusy(false); }
  };

  const finish = () => {
    onDone?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bulk-stock-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg flex items-center gap-2">
              <UploadSimple size={18} weight="bold" className="text-emerald-600" />
              Bulk Import Store Inventory
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Upload a mybillbook / stock-summary CSV. Columns auto-detected. Missing SKUs auto-created in master.
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="p-2 hover:bg-slate-100 rounded-md disabled:opacity-40"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {phase === "pick" && (
            <div className="border-2 border-dashed border-slate-300 rounded-md p-10 text-center hover:border-emerald-400 transition-colors">
              <FileCsv size={48} weight="thin" className="mx-auto text-slate-400" />
              <div className="mt-3 font-display font-bold text-slate-700">Select a CSV file</div>
              <div className="text-xs text-slate-500 mt-1">
                Expected columns: <span className="font-mono-num">Name, Item Code, Purchase Price, Selling Price, Stock Quantity, Item Category Name, MRP</span>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => onFile(e.target.files[0])}
                className="hidden"
                data-testid="bulk-stock-file"
              />
              <button
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                data-testid="bulk-stock-pick-btn"
                className="mt-4 inline-flex items-center gap-2 h-10 px-5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-md disabled:opacity-40"
              >
                {busy ? <><Spinner size={14} className="animate-spin" /> Parsing…</> : <><UploadSimple size={14} weight="bold" /> Choose CSV</>}
              </button>
            </div>
          )}

          {phase === "preview" && preview && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">Rows detected</div>
                  <div className="font-display font-bold text-2xl font-mono-num text-emerald-900 mt-0.5" data-testid="bulk-preview-rows">{preview.total_rows}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-700">Total units</div>
                  <div className="font-display font-bold text-2xl font-mono-num text-slate-900 mt-0.5">{preview.units_total.toLocaleString("en-IN")}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-700">File</div>
                  <div className="font-mono-num text-xs text-slate-700 truncate mt-1" title={file?.name}>{file?.name}</div>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Column mapping</div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-[11px] font-mono-num grid grid-cols-2 gap-y-1 gap-x-3">
                  {Object.entries(preview.mapping).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1.5">
                      <span className="text-slate-500 w-20 inline-block">{k}:</span>
                      <span className={v ? "text-emerald-700 font-semibold" : "text-amber-600"}>{v || "(not found)"}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">First {preview.preview_rows.length} rows</div>
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="text-left px-2 py-2 font-semibold">SKU</th>
                        <th className="text-left px-2 py-2 font-semibold">Name</th>
                        <th className="text-left px-2 py-2 font-semibold">Category</th>
                        <th className="text-right px-2 py-2 font-semibold">Qty</th>
                        <th className="text-right px-2 py-2 font-semibold">Selling</th>
                        <th className="text-right px-2 py-2 font-semibold">MRP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview_rows.map((r, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-2 py-1.5 font-mono-num text-slate-900">{r.sku}</td>
                          <td className="px-2 py-1.5">{r.name}</td>
                          <td className="px-2 py-1.5 text-slate-600">{r.category || "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num font-semibold">{r.qty}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num">{inr(r.selling_price)}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num">{inr(r.mrp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Import mode</div>
                  <div className="flex gap-3">
                    <label className={`flex-1 cursor-pointer border rounded-md p-3 ${mode === "set" ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-white hover:border-slate-400"}`}>
                      <input type="radio" value="set" checked={mode === "set"} onChange={() => setMode("set")} data-testid="bulk-mode-set" className="mr-2" />
                      <span className="font-semibold text-sm">Set (overwrite)</span>
                      <div className="text-[11px] text-slate-500 mt-0.5">Overwrites store qty to CSV value. Best for stock-snapshot reloads.</div>
                    </label>
                    <label className={`flex-1 cursor-pointer border rounded-md p-3 ${mode === "add" ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-white hover:border-slate-400"}`}>
                      <input type="radio" value="add" checked={mode === "add"} onChange={() => setMode("add")} data-testid="bulk-mode-add" className="mr-2" />
                      <span className="font-semibold text-sm">Add (increment)</span>
                      <div className="text-[11px] text-slate-500 mt-0.5">Increments store qty by CSV value. Best for incremental inwarding.</div>
                    </label>
                  </div>
                </div>
                <div className="flex gap-4 items-end">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Default GST % (for new SKUs)</div>
                    <input type="number" step="0.1" value={defaultGst} onChange={(e) => setDefaultGst(parseFloat(e.target.value) || 0)} data-testid="bulk-default-gst" className="w-24 h-9 px-2 border border-slate-300 rounded-md text-sm font-mono-num" />
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer h-9">
                    <input type="checkbox" checked={autoCreate} onChange={(e) => setAutoCreate(e.target.checked)} data-testid="bulk-auto-create" className="w-4 h-4" />
                    <span>Auto-create missing SKUs in master</span>
                  </label>
                </div>
              </div>
            </>
          )}

          {phase === "done" && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-md p-4">
                <CheckCircle size={36} weight="fill" className="text-emerald-600" />
                <div>
                  <div className="font-display font-bold text-lg text-emerald-900">Import complete</div>
                  <div className="text-xs text-emerald-700 mt-0.5">
                    {result.inventory_set} SKUs · {result.units_total} units · mode = <span className="font-bold uppercase">{result.mode}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Rows seen</div>
                  <div className="font-display font-bold text-xl font-mono-num mt-0.5">{result.rows_seen}</div>
                </div>
                <div className="bg-white border border-emerald-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">SKUs created</div>
                  <div className="font-display font-bold text-xl font-mono-num text-emerald-700 mt-0.5" data-testid="bulk-result-created">{result.skus_created}</div>
                </div>
                <div className="bg-white border border-blue-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-blue-700">SKUs enriched</div>
                  <div className="font-display font-bold text-xl font-mono-num text-blue-700 mt-0.5">{result.skus_updated_in_master}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Inventory rows set</div>
                  <div className="font-display font-bold text-xl font-mono-num mt-0.5">{result.inventory_set}</div>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <div className="text-xs font-bold text-amber-900 inline-flex items-center gap-1.5 mb-1">
                    <Warning size={13} weight="fill" />{result.errors.length} rows skipped
                  </div>
                  <pre className="text-[11px] font-mono-num text-amber-800 max-h-32 overflow-auto bg-white p-2 rounded">{JSON.stringify(result.errors.slice(0, 50), null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          {phase === "pick" && (
            <button onClick={onClose} disabled={busy} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-40">Cancel</button>
          )}
          {phase === "preview" && (
            <>
              <button onClick={() => { setFile(null); setPreview(null); setPhase("pick"); }} disabled={busy} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-40">Choose different file</button>
              <button
                onClick={runImport}
                disabled={busy || preview.total_rows === 0}
                data-testid="bulk-stock-import-btn"
                className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {busy ? <><Spinner size={14} className="animate-spin" /> Importing…</> : <><CheckCircle size={14} weight="bold" /> Import {preview.total_rows} rows</>}
              </button>
            </>
          )}
          {phase === "done" && (
            <button onClick={finish} data-testid="bulk-stock-close-btn" className="h-10 px-5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-md">Done</button>
          )}
        </div>
      </div>
    </div>
  );
}
